import { useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, RotateCcw, Check, Eraser } from "lucide-react";
import { Button } from "@/components/ui/button";

const OPS = {
  add: { sym: "+", label: "Addition", kreol: "Adisyon" },
  sub: { sym: "−", label: "Subtraction", kreol: "Soustraksyon" },
  mul: { sym: "×", label: "Multiplication", kreol: "Miltiplikasyon" },
  div: { sym: "÷", label: "Division", kreol: "Divizyon" },
};

const onlyDigits = (s) => (s || "").replace(/[^0-9]/g, "").slice(0, 6);

export default function MathPad() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const op = OPS[params.get("op")] ? params.get("op") : "add";
  const meta = OPS[op];

  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [setup, setSetup] = useState(false);
  const [carry, setCarry] = useState([]);
  const [ans, setAns] = useState([]);
  const [quotient, setQuotient] = useState("");
  const [remainder, setRemainder] = useState("");
  const [feedback, setFeedback] = useState(null);

  const na = parseInt(a || "0", 10);
  const nb = parseInt(b || "0", 10);

  const expected = useMemo(() => {
    if (op === "add") return na + nb;
    if (op === "sub") return na - nb;
    if (op === "mul") return na * nb;
    return null;
  }, [op, na, nb]);

  const cols = useMemo(() => {
    if (op === "div") return 0;
    const w = Math.max(String(Math.abs(expected || 0)).length, a.length, b.length, 1);
    return w;
  }, [op, expected, a, b]);

  const pad = (s, n) => s.padStart(n, " ").split("");

  const begin = () => {
    if (!a || !b) return;
    if (op === "sub" && na < nb) { setFeedback({ ok: false, msg: "Make the top number bigger than the bottom one." }); return; }
    if (op === "div" && nb === 0) { setFeedback({ ok: false, msg: "You cannot divide by zero." }); return; }
    setFeedback(null);
    setCarry(Array(cols).fill(""));
    setAns(Array(op === "div" ? 0 : cols).fill(""));
    setQuotient(""); setRemainder("");
    setSetup(true);
  };

  const reset = () => { setSetup(false); setA(""); setB(""); setFeedback(null); };
  const clearWork = () => { setCarry(Array(cols).fill("")); setAns(Array(cols).fill("")); setQuotient(""); setRemainder(""); setFeedback(null); };

  const check = () => {
    const empty = op === "div" ? !quotient.trim() : ans.every((x) => !String(x).trim());
    if (empty) { setFeedback({ ok: false, msg: "Do the exercise first: write your working and your answer in the boxes, then press Check. / Fais d'abord l'exercice, puis appuie sur Check." }); return; }
    if (op === "div") {
      const eq = Math.floor(na / nb), er = na % nb;
      const ok = parseInt(quotient || "-1", 10) === eq && parseInt(remainder || "0", 10) === er;
      setFeedback(ok
        ? { ok: true, msg: "Correct! Great working." }
        : { ok: false, msg: "Not yet, check your working and try again. Keep going! / Pas encore, vérifie ton calcul et réessaie !" });
      return;
    }
    const joined = parseInt(ans.join("").replace(/[^0-9]/g, "") || "-1", 10);
    const ok = joined === expected;
    setFeedback(ok
      ? { ok: true, msg: "Correct! Great working." }
      : { ok: false, msg: "Not yet, check your working and try again. Persevere, you can do it! / Pas encore, vérifie ton calcul et réessaie !" });
  };

  const setCell = (arr, setArr, i, v) => {
    const next = [...arr]; next[i] = onlyDigits(v).slice(-1); setArr(next);
  };

  const Cell = ({ value, onChange, testid, tone = "answer", small }) => (
    <input
      value={value} onChange={(e) => onChange(e.target.value)} inputMode="numeric" maxLength={1}
      data-testid={testid}
      className={`text-center font-kids font-bold rounded-lg border-2 border-slate-900 outline-none focus:ring-4 focus:ring-blue-200 ${small ? "w-9 h-9 text-sm bg-amber-50 text-amber-700" : "w-12 h-12 text-2xl bg-white"} ${tone === "answer" ? "" : ""}`}
    />
  );

  return (
    <div className="min-h-screen bg-[#EAF7F4] font-kids text-slate-900">
      <header className="max-w-3xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(`/kid/${childId}`)} className="rounded-full font-parent" data-testid="pad-back">
          <ArrowLeft size={18} className="mr-1" /> Back
        </Button>
        <span className="font-bold text-lg">{meta.label} pad · <span className="text-slate-500">{meta.kreol}</span></span>
      </header>

      <div className="max-w-3xl mx-auto px-5 pt-6 pb-16">
        {/* operation switch */}
        <div className="flex gap-2 mb-6">
          {Object.entries(OPS).map(([k, v]) => (
            <button key={k} onClick={() => navigate(`/kid/${childId}/pad?op=${k}`)}
              data-testid={`pad-op-${k}`}
              className={`w-12 h-12 rounded-xl border-4 border-slate-900 font-bold text-xl transition-transform hover:translate-y-0.5 ${k === op ? "bg-blue-500 text-white" : "bg-white"}`}
              style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
              {v.sym}
            </button>
          ))}
        </div>

        {!setup ? (
          <div className="nb-card bg-white p-6 space-y-4">
            <p className="font-parent text-slate-600">Type two numbers, then work it out in the copybook like at school.</p>
            <div className="flex items-center gap-3">
              <input value={a} onChange={(e) => setA(onlyDigits(e.target.value))} placeholder="0" data-testid="pad-input-a"
                className="w-32 h-14 text-center text-2xl font-bold rounded-xl border-4 border-slate-900 outline-none" />
              <span className="text-3xl font-bold">{meta.sym}</span>
              <input value={b} onChange={(e) => setB(onlyDigits(e.target.value))} placeholder="0" data-testid="pad-input-b"
                className="w-32 h-14 text-center text-2xl font-bold rounded-xl border-4 border-slate-900 outline-none" />
            </div>
            <Button onClick={begin} data-testid="pad-start" className="rounded-full bg-slate-900 hover:bg-slate-800">Set up my copybook</Button>
            {feedback && !feedback.ok && <p className="font-parent text-red-600">{feedback.msg}</p>}
          </div>
        ) : (
          <div className="nb-card bg-white p-6">
            {op !== "div" ? (
              <div className="inline-block min-w-min max-w-full overflow-x-auto">
                <div className="inline-block">
                {/* carry / borrow row */}
                <div className="flex gap-2 justify-end mb-1">
                  {carry.map((c, i) => (
                    <Cell key={i} value={c} onChange={(v) => setCell(carry, setCarry, i, v)} testid={`pad-carry-${i}`} small />
                  ))}
                </div>
                <p className="text-right text-[10px] font-parent text-amber-600 mb-2 pr-1">{op === "sub" ? "borrow" : "carry"}</p>
                {/* operand a */}
                <div className="flex gap-2 justify-end">
                  {pad(a, cols).map((d, i) => <div key={i} className="w-12 h-12 flex items-center justify-center text-2xl font-bold">{d.trim()}</div>)}
                </div>
                {/* operand b with operator */}
                <div className="flex gap-2 justify-end items-center">
                  <span className="text-2xl font-bold mr-1">{meta.sym}</span>
                  {pad(b, cols).map((d, i) => <div key={i} className="w-12 h-12 flex items-center justify-center text-2xl font-bold">{d.trim()}</div>)}
                </div>
                <div className="border-t-4 border-slate-900 my-2" />
                {/* answer cells */}
                <div className="flex gap-2 justify-end">
                  {ans.map((c, i) => (
                    <Cell key={i} value={c} onChange={(v) => setCell(ans, setAns, i, v)} testid={`pad-ans-${i}`} />
                  ))}
                </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-3xl font-bold">{na} ÷ {nb} = ?</p>
                <div className="flex items-center gap-3 font-parent">
                  <label className="text-sm text-slate-500 w-24">Quotient</label>
                  <input value={quotient} onChange={(e) => setQuotient(onlyDigits(e.target.value))} data-testid="pad-quotient"
                    className="w-28 h-12 text-center text-xl font-bold rounded-xl border-4 border-slate-900 outline-none" />
                </div>
                <div className="flex items-center gap-3 font-parent">
                  <label className="text-sm text-slate-500 w-24">Remainder</label>
                  <input value={remainder} onChange={(e) => setRemainder(onlyDigits(e.target.value))} data-testid="pad-remainder"
                    className="w-28 h-12 text-center text-xl font-bold rounded-xl border-4 border-slate-900 outline-none" />
                </div>
              </div>
            )}

            {feedback && (
              <p className={`mt-5 font-parent font-semibold ${feedback.ok ? "text-green-600" : "text-red-600"}`} data-testid="pad-feedback">
                {feedback.msg}
              </p>
            )}

            <div className="flex flex-wrap gap-3 mt-6">
              <Button onClick={check} data-testid="pad-check" className="rounded-full bg-green-600 hover:bg-green-700"><Check size={16} className="mr-1" /> Check</Button>
              <Button onClick={clearWork} variant="outline" data-testid="pad-clear" className="rounded-full"><Eraser size={16} className="mr-1" /> Clear working</Button>
              <Button onClick={reset} variant="ghost" data-testid="pad-new" className="rounded-full"><RotateCcw size={16} className="mr-1" /> New sum</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
